import { NavigateFunction } from 'react-router-dom';
import { shippingLabelEndpointPrefix } from '../config';

// API endpoints
const API_BASE_URL = `${shippingLabelEndpointPrefix}/api/labeling`;
export const AUTH_ENDPOINTS = {
  login: `${API_BASE_URL}/customer/auth/login`,
  register: `${API_BASE_URL}/customer/auth/register`,
  updatePassword: `${API_BASE_URL}/customer/auth/change-password`,
  changePassword: `${API_BASE_URL}/customer/auth/change-password`,
  setPassword: `${API_BASE_URL}/customer/auth/set-password`,
  sendVerification: `${API_BASE_URL}/customer/auth/send-verification`,
  userProfile: `${API_BASE_URL}/customer/user/user-profile`,
  logout: `${API_BASE_URL}/customer/auth/logout`,
  updateUser: `${API_BASE_URL}/customer/user/update-profile`,
  unifiedLogin: `${API_BASE_URL}/customer/auth/unified-login`,
  bindGoogle: `${API_BASE_URL}/customer/auth/bind/google`,
  bindWeChat: `${API_BASE_URL}/customer/auth/bind/wechat`,
  unbind: `${API_BASE_URL}/customer/auth/unbind`,
  authMethods: `${API_BASE_URL}/customer/auth/auth-methods`,
};

// ==================== LocalStorage Keys ====================
// Centralized constants for all auth-related localStorage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  IS_AUTHENTICATED: 'isAuthenticated',
  USER: 'user',
  USER_ROLE: 'userRole',
  // Legacy keys - for backward compatibility during migration
  LEGACY_TOKEN: 'authToken',
  LEGACY_AUTHENTICATED: 'isAuthenticated',
  LEGACY_USER: 'user',
  LEGACY_USER_ROLE: 'userRole',
  // Admin-related (to be deprecated)
  IS_ADMIN: 'isLabelAdmin',
  ADMIN_KEY: 'adminKey',
  // Matching system (fully legacy/obsolete)
  MATCHING_AUTHENTICATED: 'isMatchingAuthenticated',
  MATCHING_TOKEN: 'matchingToken',
  MATCHING_USER: 'matchingUser',
};

interface ApiResponse {
  success: boolean;
  message: string;
  [key: string]: any;
}

interface AuthPayload {
  token?: string;
  user_id?: number;
  user_role?: string;
  user_level?: string;
  profile?: {
    user_name?: string;
    phone?: string;
    email?: string;
    avatar_url?: string;
  };
}

export const persistAuthSession = (payload: AuthPayload, action: 'login' | 'impersonate' = 'login') => {
  if (!payload?.token || !payload?.user_id) {
    return false;
  }

  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, payload.token);
  localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, 'true');
  localStorage.removeItem(STORAGE_KEYS.IS_ADMIN);
  localStorage.removeItem(STORAGE_KEYS.ADMIN_KEY);

  if (payload.user_role) {
    localStorage.setItem(STORAGE_KEYS.USER_ROLE, payload.user_role);
  } else {
    localStorage.removeItem(STORAGE_KEYS.USER_ROLE);
  }

  localStorage.setItem(
    STORAGE_KEYS.USER,
    JSON.stringify({
      user_id: payload.user_id,
      user_role: payload.user_role || '',
      user_level: payload.user_level || 'LEVEL_0',
      user_name: payload.profile?.user_name || '',
      phone: payload.profile?.phone || '',
      email: payload.profile?.email || '',
      avatar_url: payload.profile?.avatar_url || '',
    }),
  );

  window.dispatchEvent(
    new CustomEvent('authStateChange', {
      detail: { isAuthenticated: true, action, system: 'labeling' },
    }),
  );

  return true;
};

export const loginUser = async (
  identifier: string,
  password: string,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.unifiedLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        method: 'password',
        credentials: {
          identifier,
          password,
        },
      }),
      credentials: 'include',
    });

    const data = await response.json();

    if (response.ok && data.success && persistAuthSession(data.data, 'login')) {
      return {
        success: true,
        message: data.message || '登录成功',
        ...data,
      };
    }

    return {
      success: false,
      message: data.message || '登录失败',
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

export const registerUser = async (
  phone: string,
  password: string,
  code: string,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.register, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password, code }),
      credentials: 'include',
    });

    const data = await response.json();

    return {
      success: response.ok,
      message: response.ok ? '注册成功' : data.message || '注册失败',
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

export const updatePassword = async (
  phone: string,
  password: string,
  code: string,
  navigate?: NavigateFunction,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.updatePassword, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password, code }),
      credentials: 'include',
    });

    const data = await response.json();

    if (response.ok) {
      // Log out the user on successful password change
      try {
        await fetch(AUTH_ENDPOINTS.logout, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      } catch (logoutError) {
        console.error(
          'Error during logout after password change:',
          logoutError,
        );
      }

      // Clear local storage regardless of server logout response
      localStorage.removeItem('authToken');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('isLabelAdmin');
      localStorage.removeItem('adminKey');

      if (navigate) {
        navigate('/'); // Redirect to home page; user will use modal for re-login
      }

      return {
        success: true,
        message: '密码修改成功，请重新登录',
        ...data,
      };
    } else {
      return {
        success: false,
        message: data.message || '密码修改失败',
        ...data,
      };
    }
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Change password for authenticated user (no old password required)
 */
export const changePassword = async (
  newPassword: string,
  confirmPassword: string,
): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }
    const response = await fetch(AUTH_ENDPOINTS.changePassword, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
      credentials: 'include',
    });

    const data = await response.json();

    if (response.ok && data.success !== false) {
      return {
        success: true,
        message: data.message || '密码修改成功',
        ...data,
      };
    } else {
      return {
        success: false,
        message: data.message || '密码修改失败',
        ...data,
      };
    }
  } catch (error) {
    console.error('Change password error:', error);
    return {
      success: false,
      message: '网络错误',
    };
  }
};

export const getVerificationCode = async (
  phone: string,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.sendVerification, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
      credentials: 'include',
    });

    const data = await response.json();

    return {
      success: response.ok,
      message: response.ok ? '验证码已发送' : data.message || '验证码发送失败',
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '验证码发送失败',
    };
  }
};

export const logoutUser = async (
  navigate: NavigateFunction,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.logout, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await response.json();

    // Regardless of server response, clean up local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isLabelAdmin');
    localStorage.removeItem('adminKey');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('labelingUser');

    // Dispatch auth state change event
    window.dispatchEvent(new CustomEvent('authStateChange', {
      detail: { isAuthenticated: false, action: 'logout', system: 'labeling' }
    }));

    // Remove automatic navigation - let the caller handle it
    return {
      success: response.ok,
      message: response.ok ? '用户退出成功' : data.message || '用户退出失败',
      ...data,
    };
  } catch (error) {
    // Still clean up local storage even if there's an error
    localStorage.removeItem('authToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isLabelAdmin');
    localStorage.removeItem('adminKey');

    // Dispatch auth state change event
    window.dispatchEvent(new CustomEvent('authStateChange', {
      detail: { isAuthenticated: false, action: 'logout', system: 'labeling' }
    }));

    return {
      success: false,
      message: '网络错误，请稍后再试',
    };
  }
};


/**
 * Unified Google login for labeling system using /customer/auth/unified-login.
 *
 * This does NOT change the existing phone/password login flow; it only adds
 * Google OAuth as an additional way to log in.
 */
export const unifiedGoogleLogin = async (
  googleToken: string,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.unifiedLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        method: 'google',
        credentials: {
          google_token: googleToken,
        },
      }),
    });

    const data = await response.json();

    if (response.ok && data.success && data.data?.token) {
      localStorage.setItem('authToken', data.data.token);
      localStorage.setItem('isAuthenticated', 'true');

      // Dispatch auth state change event
      window.dispatchEvent(
        new CustomEvent('authStateChange', {
          detail: { isAuthenticated: true, action: 'login', system: 'labeling' },
        }),
      );

      return {
        success: true,
        message: data.message || '登录成功',
        ...data,
      };
    }

    return {
      success: false,
      message: data.message || '登录失败',
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误，请稍后再试',
    };
  }
};

export const unifiedWeChatLogin = async (
  wechatCode: string,
): Promise<ApiResponse> => {
  try {
    const response = await fetch(AUTH_ENDPOINTS.unifiedLogin, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        method: 'wechat',
        credentials: {
          wechat_code: wechatCode,
        },
      }),
    });

    const data = await response.json();

    if (response.ok && data.success && data.data?.token) {
      // Store authentication state (reuse existing keys)
      localStorage.setItem('authToken', data.data.token);
      localStorage.setItem('isAuthenticated', 'true');

      // Clear old admin status and store user role from backend if available
      localStorage.removeItem('isLabelAdmin');
      localStorage.removeItem('adminKey');
      localStorage.removeItem('userRole');

      if (data.data.profile?.user_role) {
        localStorage.setItem('userRole', data.data.profile.user_role);
      }

      return {
        success: true,
        message: data.message || '登录成功',
        ...data,
      };
    }

    return {
      success: false,
      message: data.message || '登录失败',
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Fetch the current user's authentication methods summary.
 */
export const getAuthMethods = async (): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }

    const response = await fetch(AUTH_ENDPOINTS.authMethods, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message:
        data.message ||
        (response.ok ? '获取登录方式成功' : '获取登录方式失败'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Bind a Google account to the current logged-in user.
 */
export const bindGoogle = async (googleToken: string): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }

    const response = await fetch(AUTH_ENDPOINTS.bindGoogle, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ google_token: googleToken }),
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message:
        data.message ||
        (response.ok ? 'Google 账号绑定成功' : 'Google 账号绑定失败'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Bind a WeChat account to the current logged-in user.
 */
export const bindWeChat = async (wechatCode: string): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }

    const response = await fetch(AUTH_ENDPOINTS.bindWeChat, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ wechat_code: wechatCode }),
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message:
        data.message ||
        (response.ok ? '微信账号绑定成功' : '微信账号绑定失败'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Unbind an OAuth method ('google' or 'wechat') from the current user.
 */
export const unbindOAuth = async (method: 'google' | 'wechat'): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }

    const response = await fetch(`${AUTH_ENDPOINTS.unbind}/${method}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message:
        data.message ||
        (response.ok ? '解绑成功' : '解绑失败'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

/**
 * Set password for an OAuth-only user (first-time password).
 */
export const setPassword = async (
  password: string,
  userName: string,
): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return {
        success: false,
        message: '请先登录',
      };
    }

    const payload: Record<string, string> = {
      password,
      user_name: userName,
    };

    const response = await fetch(AUTH_ENDPOINTS.setPassword, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message:
        data.message ||
        (response.ok ? '密码设置成功' : '密码设置失败'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误',
    };
  }
};

// Request deduplication: share a single in-flight promise instead of aborting
let pendingProfileRequest: Promise<ApiResponse> | null = null;

export const getUserProfile = async (): Promise<ApiResponse> => {
  // If there's already a request in flight, return the same promise
  if (pendingProfileRequest) {
    return pendingProfileRequest;
  }

  const fetchProfile = async (): Promise<ApiResponse> => {
    try {
      const labelingToken = localStorage.getItem('authToken');

      const response = await fetch(AUTH_ENDPOINTS.userProfile, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(labelingToken ? { Authorization: `Bearer ${labelingToken}` } : {}),
        },
      });

      if (response.status === 404) {
        console.error('Endpoint not found:', AUTH_ENDPOINTS.userProfile);
        return {
          success: false,
          message: 'API endpoint not found',
          status: 404,
        };
      }

      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed when fetching profile');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('authToken');
        localStorage.removeItem('isLabelAdmin');
        localStorage.removeItem('adminKey');
        return {
          success: false,
          message: '会话已过期，请重新登录',
          status: response.status,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          message: '获取用户信息失败',
          status: response.status,
        };
      }

      const data = await response.json();

      if (data.success === true && data.data) {
        if (data.data.user_role) {
          localStorage.setItem('userRole', data.data.user_role);
        }
        localStorage.setItem('user', JSON.stringify(data.data));

        // Dispatch user profile update event
        window.dispatchEvent(new CustomEvent('userProfileUpdate', {
          detail: { user: data.data }
        }));

        return {
          success: true,
          message: data.message || '获取用户信息成功',
          ...data,
          user_id: data.data.user_id,
          phone: data.data.phone,
          user_name: data.data.user_name,
          user_role: data.data.user_role,
          registered_at: data.data.registered_at,
          last_login: data.data.last_login,
          oauth_profile_hint: data.data.oauth_profile_hint,
        };
      }

      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return {
        success: false,
        message: '无法连接到服务器',
        error: error instanceof Error ? error.message : String(error),
      };
    } finally {
      pendingProfileRequest = null;
    }
  };

  pendingProfileRequest = fetchProfile();
  return pendingProfileRequest;
};

export const updateUser = async (
  _userId: string,
  userName?: string,
  email?: string,
  phone?: string | null,
): Promise<ApiResponse> => {
  try {
    const token = localStorage.getItem('authToken');

    const payload: Record<string, string | null> = {};
    if (typeof userName === 'string') {
      payload.user_name = userName;
    }
    if (typeof email === 'string') {
      payload.email = email;
    }
    if (typeof phone === 'string') {
      payload.phone = phone;
    } else if (phone === null) {
      payload.phone = null;
    }

    if (Object.keys(payload).length === 0) {
      return {
        success: false,
        message: '未提供更新内容',
      };
    }

    const response = await fetch(AUTH_ENDPOINTS.updateUser, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: response.ok && data.success !== false,
      message: data.message || (response.ok ? '用户信息更新成功' : '用户信息更新错误'),
      ...data,
    };
  } catch (error) {
    return {
      success: false,
      message: '网络错误，请稍后再试',
    };
  }
};

export const isAuthenticated = (): boolean => {
  return localStorage.getItem('isAuthenticated') === 'true';
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

export const checkAuthStatus = async (): Promise<boolean> => {
  try {
    // Check localStorage first to avoid unnecessary API calls
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const token = localStorage.getItem('authToken');
    const hasUserInfo = !!localStorage.getItem('user');

    // If we have a token, are authenticated, AND have user info, return true
    // If user info is missing, we need to fetch profile to populate it
    if (isAuthenticated && token && hasUserInfo) {
      return true;
    }

    // If no token, we're definitely not authenticated
    if (!token) {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('isLabelAdmin');
      localStorage.removeItem('adminKey');
      return false;
    }

    // Only make API call if we have a token but localStorage says we're not authenticated
    const response = await fetch(AUTH_ENDPOINTS.userProfile, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });



    if (response.status === 401) {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminKey');
      localStorage.removeItem('userRole');
      return false;
    }

    const data = await response.json();
    const isAuth = data.success === true;

    // Update localStorage based on auth status
    if (isAuth) {
      localStorage.setItem('isAuthenticated', 'true');
    } else {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminKey');
      localStorage.removeItem('userRole');
    }

    return isAuth;
  } catch (error) {
    console.error('Auth check error:', error);
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('isLabelAdmin');
    localStorage.removeItem('adminKey');
    return false;
  }
};

// Add reusable admin validation function
export const validateAdminStatus = async (): Promise<boolean> => {
  try {
    const token = localStorage.getItem('authToken');

    if (!token) {
      return false;
    }

    const adminCheckUrl = `${shippingLabelEndpointPrefix}/api/labeling/customer/finance/admin/balance-threshold/current-user`;
    const response = await fetch(adminCheckUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    // If we get 403, user is not admin
    if (response.status === 403) {
      return false;
    }

    // If we get 401, token is invalid
    if (response.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userRole');
      return false;
    }

    // If we get 200, user is admin
    return response.status === 200;
  } catch (error) {
    console.error('Admin validation error:', error);
    return false;
  }
};

// ==================== Migration Helpers ====================
/**
 * Migrate legacy localStorage keys to new standardized keys
 * Called once on app initialization
 */
export const migrateLegacyStorage = (): void => {
  // Prevent self-deletion if legacy and new keys are identical
  if (STORAGE_KEYS.LEGACY_TOKEN === STORAGE_KEYS.AUTH_TOKEN) {
    // Just clean up fully obsolete keys
    const obsoleteKeys = [
      'isMatchingAuthenticated',
      'matchingToken',
      'matchingUser',
      'matchingUserRole',
      'matchingAdminKey',
      'isMatchingAdmin',
      'sseeaa_matching_token'
    ];
    obsoleteKeys.forEach(key => localStorage.removeItem(key));
    return;
  }

  // Migrate token
  const legacyToken = localStorage.getItem(STORAGE_KEYS.LEGACY_TOKEN);
  if (legacyToken && !localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, legacyToken);
  }

  // Migrate auth status
  const legacyAuth = localStorage.getItem(STORAGE_KEYS.LEGACY_AUTHENTICATED);
  if (legacyAuth && !localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED)) {
    localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, legacyAuth);
  }

  // Migrate user data
  const legacyUser = localStorage.getItem(STORAGE_KEYS.LEGACY_USER);
  if (legacyUser && !localStorage.getItem(STORAGE_KEYS.USER)) {
    localStorage.setItem(STORAGE_KEYS.USER, legacyUser);
  }

  // Migrate user role
  const legacyRole = localStorage.getItem(STORAGE_KEYS.LEGACY_USER_ROLE);
  if (legacyRole && !localStorage.getItem(STORAGE_KEYS.USER_ROLE)) {
    localStorage.setItem(STORAGE_KEYS.USER_ROLE, legacyRole);
  }

  // Clean up legacy keys after successful migration
  if (localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)) {
    localStorage.removeItem(STORAGE_KEYS.LEGACY_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_AUTHENTICATED);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_USER);
    localStorage.removeItem(STORAGE_KEYS.LEGACY_USER_ROLE);
  }

  // Clean up old matching system keys (fully obsolete)
  const obsoleteKeys = [
    'isMatchingAuthenticated',
    'matchingToken',
    'matchingUser',
    'matchingUserRole',
    'matchingAdminKey',
    'isMatchingAdmin',
    'sseeaa_matching_token'
  ];
  obsoleteKeys.forEach(key => localStorage.removeItem(key));
};

// ==================== Legacy Aliases ====================
// For backward compatibility during migration
export const isLabelingAuthenticated = isAuthenticated;
export const getLabelingAuthToken = getAuthToken;
